import * as React from 'react';
import { Button, ButtonGroup, Form, Modal } from 'react-bootstrap';
import DivisionDropDown from '../DivisionDropDown';

interface SeriesClass {
	id: string;
	name: string;
	seriesId: string;
	divisionId?: string | null;
	teamSize: number;
	lookupName: string;
	pointsScope: number;
}

interface Division {
	id: string;
	name: string;
}

interface ClassesTabProps {
	seriesId: string;
	classes: SeriesClass[];
	divisions: Division[];
	editingClass: SeriesClass | null;
	isCreating: boolean;
	onStartCreate: () => void;
	onStartEdit: (seriesClass: SeriesClass) => void;
	onCancelEdit: () => void;
	onSave: () => void;
	onDelete: (seriesClass: SeriesClass) => void;
	onFieldChange: (field: keyof SeriesClass, value: any) => void;
}

export default class ClassesTab extends React.Component<ClassesTabProps> {
	getDivisionName(divisionId: string): string {
		const division = this.props.divisions.find(d => d.id === divisionId);
		return division ? division.name : 'Unknown';
	}

	getPointsScopeName(scope: number): string {
		switch (scope) {
			case 0: return 'By Class';
			case 1: return 'By Division';
			case 2: return 'All Competitors';
			default: return 'By Class';
		}
	}

	getPointsScopeBadgeClass(scope: number): string {
		switch (scope) {
			case 0: return 'bg-primary';
			case 1: return 'bg-info';
			case 2: return 'bg-warning';
			default: return 'bg-primary';
		}
	}

	render() {
		const { seriesId, classes, editingClass, isCreating, onStartCreate, onStartEdit, onCancelEdit, onSave, onDelete, onFieldChange } = this.props;

		return (
			<div className="mt-3">
				<h4>Classes</h4>
				
				<div className="mb-3">
					<Button className="btn btn-primary" onClick={onStartCreate}>
						Add New Class
					</Button>
				</div>

				<Modal show={!!editingClass} onHide={onCancelEdit} centered>
					<Modal.Header closeButton>
						<Modal.Title>{isCreating ? 'Create New Class' : 'Edit Class'}</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						{editingClass && (
							<>
								<div className="mb-3">
									<label htmlFor="className" className="form-label">Name</label>
									<input 
										type="text" 
										id="className" 
										className="form-control" 
										value={editingClass.name} 
										onChange={(e) => onFieldChange('name', e.target.value)} 
									/>
								</div>
								<div className="mb-3">
									<label htmlFor="division" className="form-label">Division</label>
									<DivisionDropDown
										seriesId={seriesId}
										value={editingClass.divisionId}
										onChange={(divisionId) => onFieldChange('divisionId', divisionId)}
									/>
									<small className="form-text text-muted">
										Optional: Assign this class to a division for better organization.
									</small>
								</div>
								<div className="mb-3">
									<label htmlFor="pointsScope" className="form-label">Points Scope</label>
									<Form.Select
										id="pointsScope"
										value={editingClass.pointsScope || 0}
										onChange={(e) => onFieldChange('pointsScope', parseInt(e.target.value))}
									>
										<option value={0}>By Class - Points based on position within this class only</option>
										<option value={1} disabled={!editingClass.divisionId}>
											By Division - Points based on position within the division
											{!editingClass.divisionId && ' (requires division)'}
										</option>
										<option value={2}>All Competitors - Points based on overall position in the race</option>
									</Form.Select>
									<small className="form-text text-muted">
										<strong>By Class:</strong> Traditional scoring - competitors ranked within their class.<br />
										<strong>By Division:</strong> Prevents "sandbagging" - all division competitors ranked together for points.<br />
										<strong>All Competitors:</strong> Overall race position - useful for time trials or championships.
									</small>
								</div>
								<div className="mb-3">
									<label htmlFor="lookupName" className="form-label">Lookup Name</label>
									<input 
										type="text" 
										id="lookupName" 
										className="form-control" 
										value={editingClass.lookupName || ''} 
										onChange={(e) => onFieldChange('lookupName', e.target.value)} 
									/>
								</div>
								<div className="mb-3">
									<label htmlFor="teamSize" className="form-label">Team Size</label>
									<input 
										type="number" 
										id="teamSize" 
										className="form-control" 
										value={editingClass.teamSize} 
										onChange={(e) => onFieldChange('teamSize', parseInt(e.target.value) || 1)} 
									/>
								</div>
							</>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={onCancelEdit}>Cancel</Button>
						<Button variant="success" onClick={onSave}>
							{isCreating ? 'Create' : 'Save'}
						</Button>
					</Modal.Footer>
				</Modal>

				<table className="table table-striped">
					<thead>
						<tr>
							<th>Name</th>
							<th>Division</th>
							<th>Points Scope</th>
							<th>Lookup Name</th>
							<th>Team Size</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{classes.map(sc =>
							<tr key={sc.id}>
								<td>{sc.name}</td>
								<td>
									{sc.divisionId ? (
										<span className="badge bg-info">{this.getDivisionName(sc.divisionId)}</span>
									) : (
										<span className="text-muted">No division</span>
									)}
								</td>
								<td>
									<span className={`badge ${this.getPointsScopeBadgeClass(sc.pointsScope || 0)}`}>
										{this.getPointsScopeName(sc.pointsScope || 0)}
									</span>
								</td>
								<td>{sc.lookupName}</td>
								<td>{sc.teamSize}</td>
								<td>
									<ButtonGroup>
										<Button className="btn btn-sm btn-primary" onClick={() => onStartEdit(sc)}>Edit</Button>
										<Button className="btn btn-sm btn-danger" onClick={() => onDelete(sc)}>Delete</Button>
									</ButtonGroup>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		);
	}
}
